import type { DocumentContext } from '../domain/DocumentContext';
import { resolveContextCustomerLocale } from '../domain/customerLocaleFormatters';
import { formatCoverDisplayDate } from './utils/formatCoverDisplayDate';
import { BASE_VARIABLE_CATALOG } from './variableCatalog';
import { sanitizeProseHtml } from './sanitizeProseHtml';

const TOKEN_RE = /\{\{\s*([\w.]+)\s*\}\}/g;
const MISSING_KNOWN_VALUE = '\u2014';

export interface ResolveTokensOptions {
  knownPaths?: ReadonlySet<string>;
  /**
   * `'html'`: the output is injected as HTML (`dangerouslySetInnerHTML`), so every substituted
   * value is escaped (`& < > " '`) and reads as text. Leave unset when the result becomes a React
   * text node — React escapes those itself, and escaping twice would print `&amp;amp;`.
   */
  escapeValues?: 'html';
  /**
   * Leaves every `{{token}}` as written. The block / template editor canvas with Sample data off
   * (`previewValueBinding: 'unbound'`) shows the author's tokens as chips instead of resolving them
   * against an empty context into nothing. Never set on the export surface (PDF parity).
   */
  keepTokens?: boolean;
}

const HTML_ESCAPES: Record<string, string> = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' };

const escapeHtml = (value: string): string => value.replace(/[&<>"']/g, (char) => HTML_ESCAPES[char] ?? char);

const normalizePath = (path: string): string => path.toLowerCase();

const collectDateTypedPaths = (categories: typeof BASE_VARIABLE_CATALOG): ReadonlySet<string> => {
  const paths = new Set<string>();
  for (const category of categories) {
    for (const variable of category.variables) {
      if (variable.type === 'date' && variable.path) {
        paths.add(normalizePath(variable.path));
      }
    }
  }
  if (paths.has('today')) paths.add('date.today');
  if (paths.has('date.today')) paths.add('today');
  return paths;
};

const BASE_DATE_PATHS = collectDateTypedPaths(BASE_VARIABLE_CATALOG);

/**
 * A list value (`customer.tags`, sent by the CRM as an array of names) prints as its items joined by a bare comma,
 * `VIP,Net-30` - the same text on the HTML preview and the PDF, since both resolve through here.
 */
const listText = (items: readonly unknown[]): string => items.filter((item) => ['string', 'number'].includes(typeof item)).join(',');

const stringifyTokenValue = (value: unknown, normalizedPath: string, context: DocumentContext): string => {
  if (Array.isArray(value)) return listText(value);
  const raw = typeof value === 'string' ? value : String(value);
  if (!BASE_DATE_PATHS.has(normalizedPath)) return raw;
  return formatCoverDisplayDate(raw, resolveContextCustomerLocale(context));
};

const resolvePath = (ctx: unknown, path: string): unknown => {
  return normalizePath(path)
    .split('.')
    .reduce<unknown>((acc, key) => {
      if (acc && typeof acc === 'object') {
        const record = acc as Record<string, unknown>;
        const matchKey = Object.keys(record).find((candidate) => candidate.toLowerCase() === key);
        if (matchKey !== undefined) {
          return record[matchKey];
        }
      }
      return undefined;
    }, ctx);
};

const resolveTokenValue = (context: DocumentContext, path: string): unknown => {
  const normalizedPath = normalizePath(path);
  if (normalizedPath === 'today' || normalizedPath === 'date.today') {
    const ctx = context as DocumentContext & { today?: unknown };
    return ctx.today ?? context.date?.today;
  }

  if (normalizedPath.startsWith('lineitems.')) {
    const field = normalizedPath.slice('lineitems.'.length);
    const items = context.lineItems;
    if (Array.isArray(items) && items.length > 0) {
      const first = items[0];
      if (first && typeof first === 'object') {
        const record = first as Record<string, unknown>;
        const matchKey = Object.keys(record).find((candidate) => candidate.toLowerCase() === field);
        if (matchKey !== undefined) {
          return record[matchKey];
        }
      }
    }
  }
  return resolvePath(context, normalizedPath);
};

export const resolveTokens = (input: string, context: DocumentContext, options?: ResolveTokensOptions): string => {
  if (typeof input !== 'string' || input.length === 0) {
    return '';
  }

  if (options?.keepTokens) return input;

  const knownPaths = options?.knownPaths;
  const normalizedKnownPaths = knownPaths ? new Set(Array.from(knownPaths, normalizePath)) : undefined;

  return input.replace(TOKEN_RE, (fullMatch, path: string) => {
    const normalizedPath = normalizePath(path);
    if (normalizedKnownPaths && !normalizedKnownPaths.has(normalizedPath)) {
      return fullMatch;
    }

    const value = resolveTokenValue(context, normalizedPath);
    if (value === undefined || value === null) {
      return knownPaths ? MISSING_KNOWN_VALUE : '';
    }

    const text = stringifyTokenValue(value, normalizedPath, context);
    return options?.escapeValues === 'html' ? escapeHtml(text) : text;
  });
};

/**
 * Resolves tokens in an HTML body that will be injected as HTML: CRM values (customer, contact,
 * job site, user, …) are data, never markup, so they are HTML-escaped; then the whole body goes through
 * the prose allowlist sanitiser (LLAMS-610 X-06), which also drops a URL a value completed into a
 * disallowed scheme (`javascript:`, …).
 */
export const resolveTokensForHtml = (
  input: string,
  context: DocumentContext,
  options?: Omit<ResolveTokensOptions, 'escapeValues'>
): string => sanitizeProseHtml(resolveTokens(input, context, { ...options, escapeValues: 'html' }));
